/**
 * Application entry point for SignalSentry client app.
 *
 * Imports global styles (reset, design tokens, fonts) before rendering.
 * Wraps the app in:
 *   1. ThemeProvider - SOC light/dark mode (Phase 4 amendment 2026-04-27)
 *   2. QueryClientProvider - TanStack Query server state management
 *   3. AuthProvider - authenticated session state and token lifecycle
 *   4. WebSocketProvider - singleton WS connection for live updates (plan 03-06)
 *   5. AppRouter - claim-aware router wrapper (Phase 3.1 ADR-006)
 *
 * Provider order is important:
 *   ThemeProvider sits outermost so any descendant can call useTheme().
 *   AuthProvider must sit outside WebSocketProvider so WS can read auth status.
 *   Both must sit outside AppRouter so route components can call useAuth()
 *   and useWebSocket(), and so AppRouter can rebuild on user claim changes.
 *
 * The refresh-on-401 interceptor in api-client.ts dispatches
 * 'signalsentry:auth:expired' events that AuthProvider listens for to update
 * the UI without a forced re-render cycle.
 *
 * FOUC prevention: ThemeProvider applies the resolved mode synchronously on
 * first render (its initial useState call runs before paint). Persisted
 * `soc-theme` localStorage value or `prefers-color-scheme` media query is
 * consulted; fallback is 'dark'.
 *
 * Runtime config boot gate (internal-cloud readiness, 2026-06-10):
 * before the first React render we await loadRuntimeConfig(), which fetches
 * the OPTIONAL /config.json deployment override file (fleet API/WS URLs +
 * map tile sources). The await guarantees that lazy route chunks - some of
 * which capture env values at module evaluation time (e.g. the satellite
 * map's tile constants) - always load AFTER any overrides are installed.
 * loadRuntimeConfig never throws and is internally time-capped, so on the
 * public DigitalOcean deployment (which ships no config.json) the only
 * effect is one fast same-origin 404/HTML-fallback fetch before first paint;
 * every value then falls back to the build-time VITE_* defaults, preserving
 * the existing behavior exactly. See src/lib/runtime-config.ts.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';

// Global CSS side effects - must come before any component render
import './design/global.css';
import './design/fonts.css';

import { loadRuntimeConfig } from './lib/runtime-config';
import { queryClient } from './lib/query-client';
import { AppRouter } from './router';
import { AuthProvider } from './features/auth/AuthProvider';
import { WebSocketProvider } from './features/realtime/WebSocketProvider';
import { ThemeProvider } from './design/ThemeProvider';
import { AppUpdateBanner } from './features/app-update/AppUpdateBanner';
import { ToastProvider } from './components/Toast';

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in DOM');
}

/**
 * Boot the SPA: settle the optional runtime deployment config FIRST, then
 * mount the provider tree. Module evaluation (everything imported above)
 * happens before the config fetch, which is safe because all entry-chunk
 * consumers of env.FLEET_API_URL / env.FLEET_WS_URL read the values at call
 * time (request / socket-connect time), never at module scope.
 */
async function boot(): Promise<void> {
  // Never throws; resolves with overrides installed or fallback confirmed.
  await loadRuntimeConfig();

  createRoot(rootEl as HTMLElement).render(
    <StrictMode>
      <ThemeProvider>
        <ToastProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <WebSocketProvider>
                <AppRouter />
              </WebSocketProvider>
            </AuthProvider>
          </QueryClientProvider>
          {/*
           * Global "new version available" toast (task #200). Sibling of AppRouter
           * so it survives router rebuilds (AppRouter rebuilds createBrowserRouter
           * on auth-claim changes) and shows on every surface incl. /login. Needs
           * no auth/query/ws context; sits inside ThemeProvider only for theme vars.
           */}
          <AppUpdateBanner />
        </ToastProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}

void boot();
